import packageJson from "../../package.json";

const APP_TITLE = "Open IFC Viewer";
const DEFAULT_HOST = "teams";
const DEFAULT_CLIENT = "web";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

class Authentication {
  static async isAccessValid(licensing, tenantId, email, host, client) {
    if (!licensing?.applyLicense || !tenantId) {
      return false;
    }

    const hasLicense = await licensing.applyLicense({
      key: tenantId,
      email: email || "",
      host,
      client,
      app: this.getTitle(),
      // Keep misspelled key for compatibility with existing licensing backend.
      verison: this.getVersion(),
    });

    return Boolean(hasLicense);
  }

  static getVersion() {
    return packageJson.version;
  }

  static getTitle() {
    return APP_TITLE;
  }
}

const normalizeHost = (hostName) => {
  const normalized = String(hostName || "").toLowerCase();
  if (!normalized) {
    return DEFAULT_HOST;
  }
  if (normalized.includes("teams")) {
    return "teams";
  }
  if (normalized.includes("outlook")) {
    return "outlook";
  }
  if (normalized.includes("office")) {
    return "office";
  }
  return normalized;
};

const normalizeClient = (clientType) => {
  const normalized = String(clientType || "").toLowerCase();
  return normalized || DEFAULT_CLIENT;
};

const resolveHostAndClient = (teamsContext) => ({
  host: normalizeHost(teamsContext?.app?.host?.name),
  client: normalizeClient(teamsContext?.app?.host?.clientType),
});

const waitForLicensingApi = async (timeoutMs = 4000, pollMs = 100) => {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if (window.ifcViewerLicensing?.applyLicense) {
      return window.ifcViewerLicensing;
    }
    await sleep(pollMs);
  }

  return window.ifcViewerLicensing;
};

const checkPremiumAccessWithNewFlow = async (tenantId, email, teamsContext) => {
  try {
    const licensing = await waitForLicensingApi();
    if (!licensing?.applyLicense) {
      console.warn("Viewer licensing API not available; skipping new auth flow.");
      return false;
    }

    const { host, client } = resolveHostAndClient(teamsContext);
    const isValid = await Authentication.isAccessValid(licensing, tenantId, email, host, client);
    if (isValid) {
      console.log("Premium access is valid via new licensing flow.");
    }
    return isValid;
  } catch (error) {
    console.error("Error checking premium access with new flow:", error);
    return false;
  }
};

export const checkPremiumAccessLegacy = async (tenantId, domain, grantedScopes = []) => {
  const version = packageJson.version;
  const scopesParam = grantedScopes.join(",");
  const requestUrl = `https://func-auth-prod-001.azurewebsites.net/api/CheckAccess?tenantId=${encodeURIComponent(
    tenantId
  )}&domain=${encodeURIComponent(domain)}&version=${encodeURIComponent(
    version
  )}&title=${encodeURIComponent(APP_TITLE)}&grantedScopes=${encodeURIComponent(scopesParam)}`;

  try {
    const response = await fetch(requestUrl, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "X-Client-Version": version,
        "X-Client-Title": APP_TITLE,
      },
    });

    if (response.status === 403) {
      console.error("Legacy premium access denied with status 403:", await response.text());
      return false;
    } else if (!response.ok) {
      console.error("Legacy premium access check failed:", await response.text());
      return true;
    } else {
      console.log("Premium access is valid via legacy auth flow.");
      return true;
    }
  } catch (error) {
    console.error("Error checking premium access with legacy flow:", error);
    return true;
  }
};

export const checkPremiumAccess = async (
  tenantId,
  domain,
  grantedScopes = [],
  email = "",
  teamsContext = null
) => {
  const hasAccessViaNewFlow = await checkPremiumAccessWithNewFlow(tenantId, email, teamsContext);
  if (hasAccessViaNewFlow) {
    return true;
  }

  // Keep legacy auth path to avoid forced user migration.
  return checkPremiumAccessLegacy(tenantId, domain, grantedScopes);
};
